import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useApp } from '../context/AppContext';
import { Printer, X } from 'lucide-react';

/**
 * Single-label print view sized exactly for a DYMO LabelWriter 1" x 1" square label
 * (DYMO 30332 / 1738595). The @page rule sizes the print page to 1in x 1in with
 * zero margins so the printer feeds and cuts one label per scan.
 *
 * URL shape:
 *   /print-label/item/:code
 *   /print-label/kit/:code
 *   Optional query: ?size=1.125x3.5 to override (future). Defaults to 1x1.
 *
 * On load: auto-opens the browser print dialog. The on-screen preview lives
 * outside the @media print rules so the user can re-print without re-navigating.
 */
export default function PrintLabel() {
  const { kind, code } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { items, kits } = useApp();
  const [autoPrinted, setAutoPrinted] = useState(false);

  const target = useMemo(() => {
    if (kind === 'item') return items.find((i) => i.code === code);
    if (kind === 'media') return items.find((i) => i.code === code);
    if (kind === 'kit') return kits.find((k) => k.code === code);
    return null;
  }, [kind, code, items, kits]);

  // Media QR encodes the full public status URL so a phone-camera scan opens
  // the mobile-friendly Ready / Verifying / Needs Offload page. Regular item
  // and kit QRs stay as raw codes so the BT scanner + in-app camera scanner
  // continue to feed the same pack/return handler.
  const qrValue = useMemo(() => {
    if (!target) return '';
    if (kind === 'media') {
      return `${window.location.origin}/m/${target.code}`;
    }
    return target.code;
  }, [kind, target]);

  const skipAuto = params.get('auto') === '0';

  const handleClose = () => {
    // Print preview is typically launched via window.open(...) as a popup.
    // Try to close that window first; fall back to history nav for direct loads.
    try {
      window.close();
    } catch (_) {
      /* ignored */
    }
    // If still open (browser blocked close on non-script-opened windows),
    // fall back to navigating back in SPA history.
    setTimeout(() => {
      if (!window.closed) navigate(-1);
    }, 50);
  };

  useEffect(() => {
    if (!target || autoPrinted || skipAuto) return;
    // Defer to next tick so QR has time to render
    const t = setTimeout(() => {
      window.print();
      setAutoPrinted(true);
    }, 350);
    return () => clearTimeout(t);
  }, [target, autoPrinted, skipAuto]);

  if (!target) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-neutral-400 mb-4">
            No {kind} found with code <span className="font-mono">{code}</span>.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="text-[11px] btn-amber px-3 py-1.5 rounded"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Print stylesheet: exactly one 1in × 1in page with no margins.
          Hides everything except the .label-print area when printing. */}
      <style>{`
        @page {
          size: 1in 1in;
          margin: 0;
        }
        @media print {
          html, body {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body * { visibility: hidden !important; }
          .label-print, .label-print * { visibility: visible !important; }
          .label-print {
            position: fixed;
            top: 0;
            left: 0;
            width: 1in;
            height: 1in;
            padding: 0.04in;
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            color: #000;
          }
          .label-print svg { width: 0.92in !important; height: 0.92in !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* On-screen preview UI (hidden when printing) */}
      <div className="min-h-screen bg-neutral-950 text-white p-8 no-print" data-testid="print-label-screen">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-amber-400 mb-1">
                Print Preview · DYMO 1&quot; × 1&quot;
              </div>
              <h1 className="text-lg text-white">
                {kind === 'kit'
                  ? 'Kit label'
                  : kind === 'media'
                  ? 'Media status label'
                  : 'Item label'}
                : {target.name}
              </h1>
              <div className="text-[11px] text-neutral-500 font-mono mt-1">{target.code}</div>
              {kind === 'media' && (
                <div className="text-[10px] text-amber-400/80 mt-1 break-all">
                  Encodes: <span className="font-mono">{qrValue}</span>
                </div>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-[11px] text-neutral-400 hover:text-white border border-neutral-800 px-2 py-1 rounded inline-flex items-center gap-1"
              data-testid="print-label-close"
            >
              <X size={11} /> Close
            </button>
          </div>

          <div className="surface rounded-lg p-6 mb-4 flex flex-col items-center">
            <div
              className="border border-dashed border-neutral-800 rounded p-1.5 bg-white"
              style={{ width: '160px', height: '160px' }}
              data-testid="print-label-qr-preview"
            >
              <QRCodeSVG
                value={qrValue}
                size={148}
                level="M"
                includeMargin={false}
              />
            </div>
            <div className="mt-3 text-[10px] text-neutral-500 text-center leading-tight">
              On the printed label the QR fills the full 1&quot; × 1&quot; — the box
              above is a visual stand-in.
              {kind === 'media' && (
                <>
                  <br />
                  Scanning this with a phone opens the public status page.
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="btn-amber rounded-md px-4 py-2 text-xs flex items-center gap-2"
              data-testid="print-label-trigger"
            >
              <Printer size={13} /> Print again
            </button>
            <p className="text-[11px] text-neutral-500 ml-2">
              Tip: in the browser print dialog, select your DYMO LabelWriter and confirm
              page size is set to <span className="text-neutral-300">1&quot; × 1&quot;</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Print-only label payload */}
      <div className="label-print">
        <QRCodeSVG value={qrValue} size={300} level="M" includeMargin={false} />
      </div>
    </>
  );
}
